/**
 * ReportHeader Component
 *
 * Header section for reports with title, filters, and export actions.
 * Hidden when printing.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FileText, FileSpreadsheet, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FloorPlanOption {
  id: string;
  name: string;
}

interface ReportHeaderProps {
  title: string;
  subtitle?: string;
  floorPlans: FloorPlanOption[];
  selectedFloorPlanId: string | 'all';
  onFloorPlanChange: (id: string | 'all') => void;
  onExportPDF: () => void;
  onExportCSV?: (section?: string) => void;
  csvSections?: { id: string; label: string }[];
  isExporting?: boolean;
  className?: string;
}

export function ReportHeader({
  title,
  subtitle,
  floorPlans,
  selectedFloorPlanId,
  onFloorPlanChange,
  onExportPDF,
  onExportCSV,
  csvSections,
  isExporting = false,
  className,
}: ReportHeaderProps) {
  return (
    <div className={cn('report-header report-controls print:hidden', className)}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Title */}
        <div>
          <h2 className="text-2xl font-semibold">{title}</h2>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Floor Plan Filter */}
          <Select
            value={selectedFloorPlanId}
            onValueChange={onFloorPlanChange}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select floor plan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Floor Plans</SelectItem>
              {floorPlans.map(fp => (
                <SelectItem key={fp.id} value={fp.id}>
                  {fp.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* PDF Export Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={onExportPDF}
            disabled={isExporting}
          >
            <FileText className="h-4 w-4 mr-2" />
            PDF
          </Button>

          {/* CSV Export Dropdown */}
          {onExportCSV && csvSections && csvSections.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExporting}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  CSV
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {csvSections.map(section => (
                  <DropdownMenuItem
                    key={section.id}
                    onClick={() => onExportCSV(section.id)}
                  >
                    {section.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}
